import {
  complement,
  darken,
  desaturate,
  getLuminance,
  lighten,
  parseToHsl,
  parseToRgb,
  readableColor,
  saturate,
} from 'polished'

const utilsColorMap = {
  success: true,
  warning: true,
  error: true,
}

export function makeColorObject(color) {
  return {
    hex: color,
    hsl: parseToHsl(color),
    rgb: parseToRgb(color),
    readable: readableColor(color),
    complemented: complement(color),
    complementedReadable: readableColor(complement(color)),
  }
}

export function makeColor(key, value) {
  let renderedColor = null
  if (!value) return renderedColor

  // Only single color properties required for each of these.
  if (utilsColorMap[key]) {
    renderedColor = {
      base: makeColorObject(value),
    }
  } else if (key === 'dark') {
    // The dark keyneeds more light shades and less dark
    renderedColor = {
      lightest: makeColorObject(lighten(0.15, value)),
      lighter: makeColorObject(lighten(0.1, value)),
      light: makeColorObject(lighten(0.05, value)),
      base: makeColorObject(value),
      darker: makeColorObject(darken(0.15, value)),
    }
  } else if (key === 'light') {
    // The light keyneeds more dark shades and less light
    renderedColor = {
      lighter: makeColorObject(lighten(0.15, value)),
      base: makeColorObject(value),
      dark: makeColorObject(darken(0.05, value)),
      darker: makeColorObject(darken(0.1, value)),
      darkest: makeColorObject(darken(0.15, value)),
    }
  } else {
    const luminance = getLuminance(value)
    /**
     * The purpose of this block is to determine if a color is very dark, and if so,
     * adjust the amount that it lightens or darkens each shade by. This is so that
     * the adjustment for darker colors is lessened so they do not become nearly black
     * and vice versa for lighter colors.
     *
     * If luminance is less than 40% it is a very dark color.
     * If the above is not true, the default threshold for light and dark is 10%.
     * If it is true, the dark threshold should be 6%, and the light threshold 14%.
     */
    const darkThreshold = luminance < 0.4 ? 0.06 : 0.1
    const lightThreshold = luminance < 0.4 ? 0.14 : 0.1

    /**
     * Each property, excluding base, is lightened and then saturated or
     * darkened and desaturated by the above determined thresholds in increments.
     * e.g. "lighter" is twice the determined threshold for light, so the lightness
     * and saturation thresholds are both doubled.
     */ renderedColor = {
      lighter: makeColorObject(
        lighten(lightThreshold * 2, saturate(lightThreshold * 2, value))
      ),
      light: makeColorObject(
        lighten(lightThreshold, saturate(lightThreshold, value))
      ),
      base: makeColorObject(value),
      dark: makeColorObject(
        darken(darkThreshold, desaturate(darkThreshold, value))
      ),
      darker: makeColorObject(
        darken(darkThreshold * 2, desaturate(darkThreshold * 2, value))
      ),
    }
  }
  return renderedColor
}

export function makeColors(colors) {
  const renderedColors = {}
  Object.entries(colors).forEach(([key, value]) => {
    const color = makeColor(key, value)
    if (color) renderedColors[key] = color
  })
  return renderedColors
}

/* eslint-disable */
export function mergeThemes(dTheme, theme = {}, isColor = false) {
  const entries = Object.entries(theme)
  for (let i = 0; i < entries.length; i++) {
    const [key, value] = entries[i]
    if (value === (undefined || null) || (!(`${key}` in dTheme) && !isColor))
      continue
    if (Object.prototype.toString.call(value) === '[object Object]') {
      dTheme[key] = mergeThemes(dTheme[key], value, key === 'colors')
    } else {
      dTheme[key] = isColor ? makeColor(key, value) : value
    }
  }
  return dTheme
}
/* eslint-enable */

export default {
  name: 'default',
  colors: makeColors({
    primary: '#1990FF',
    secondary: '#bf3eff',
    tertiary: '#2AEAFF',
    dark: '#3d3d3d',
    black: '#000000',
    white: '#ffffff',
    light: '#f0f0f0',
    warning: '#ff7928',
    error: '#ff3068',
    success: '#2de6bc',
    darkGray: '#6d6d6d',
    background1: '#edfaff',
    background2: '#d7f1ff',
    background3: '#fcf5ff',
    background4: '#f7e7ff',
    background5: '#eafffe',
    background6: '#cbffff',
  }),
  backgroundColors: {
    primary: {
      textColor: '',
      boxShadow: '',
    },
    secondary: {
      textColor: '',
      boxShadow: '',
    },
    tertiary: {
      textColor: '',
      boxShadow: '',
    },
  },
  alternateBgColor: null,
  fonts: {
    headings: {
      family: 'montserrat, sans-serif',
      lg: {
        size: [32, 52],
        lineHeight: [38, 60],
        weight: 800,
      },
      md: {
        size: [28, 48],
        lineHeight: [32, 56],
        weight: 800,
      },
      sm: {
        size: [24, 40],
        lineHeight: [28, 48],
        weight: 800,
      },
    },
    subheadings: {
      lg: {
        size: [22, 28],
        lineHeight: [24, 36],
        weight: 500,
      },
      md: {
        size: [20, 24],
        lineHeight: [24, 32],
        weight: 500,
      },
      sm: {
        size: [18, 20],
        lineHeight: [24, 28],
        weight: 500,
      },
    },
    body: {
      family: 'averta, sans-serif',
      lg: {
        size: 18,
        lineHeight: 24,
      },
      md: {
        size: 16,
        lineHeight: 22,
      },
      sm: {
        size: 14,
        lineHeight: 22,
      },
      xs: {
        size: 12,
        lineHeight: 16,
      },
      xxs: {
        size: 10,
        lineHeight: 12,
      },
    },
    links: {
      primary: {
        color: '#fff',
        activeColor: '#fff',
        visitedColor: '#fff',
        hoverColor: '#fff',
      },
      secondary: {
        color: '#fff',
        activeColor: '#fff',
        visitedColor: '#fff',
        hoverColor: '#fff',
      },
      tertiary: {
        color: '#000',
        activeColor: '#000',
        visitedColor: '#000',
        hoverColor: '#000',
      },
      light: {
        color: '#000',
        activeColor: '#000',
        visitedColor: '#000',
        hoverColor: '#000',
      },
      dark: {
        color: '#fff',
        activeColor: '#fff',
        visitedColor: '#fff',
        hoverColor: '#fff',
      },
      black: {
        color: '#fff',
        activeColor: '#fff',
        visitedColor: '#fff',
        hoverColor: '#fff',
      },
      white: {
        color: '#000',
        activeColor: '#000',
        visitedColor: '#000',
        hoverColor: '#000',
      },
    },
  },
  breakpoints: {
    xs: '480px',
    sm: '540px',
    md: '768px',
    lg: '940px',
    xl: '1200px',
  },
  spacing: {
    min: '4px',
    xxs: '8px',
    xs: '12px',
    sm: '16px',
    md: '24px',
    lg: '32px',
    xl: '48px',
    xxl: '64px',
    max: '88px',
  },
  shadow: '0 0.3rem 1rem 0 rgba(0, 0, 0, 0.1)',
  radius: '24px',
  components: {
    Breadcrumbs: {
      backgroundColor: '',
    },
    Button: {
      rounded: 'min',
      variants: {
        primary: {
          color: null,
          hoverColor: null,
          activeColor: null,
          hoverBg: null,
          activeBg: null,
        },
        secondary: {
          color: null,
          hoverColor: null,
          activeColor: null,
          hoverBg: null,
          activeBg: null,
        },
        tertiary: {
          color: null,
          hoverColor: null,
          activeColor: null,
          hoverBg: null,
          activeBg: null,
        },
        dark: {
          color: null,
          hoverColor: null,
          activeColor: null,
          hoverBg: null,
          activeBg: null,
        },
        light: {
          color: null,
          hoverColor: null,
          activeColor: null,
          hoverBg: null,
          activeBg: null,
        },
        gradients: {
          primary: {
            color: null,
            hoverColor: null,
            activeColor: null,
            hoverBg: null,
          },
          secondary: {
            color: null,
            hoverColor: null,
            activeColor: null,
            hoverBg: null,
          },
          tertiary: {
            color: null,
            hoverColor: null,
            activeColor: null,
            hoverBg: null,
          },
        },
      },
    },
    Column: {
      image: 'full',
      border: {
        topbar: {
          width: '12px',
        },
        stroke: {
          width: '3px',
        },
      },
    },
    ListItem: {
      icon: '',
    },
    Table: {
      alternateBgColor: null,
      leftColumnAlignment: '',
      radius: '',
      header: {
        color: '',
        stroke: {
          top: '',
          bottom: '',
        },
      },
      row: {
        color: '',
        alternateBgColor: null,
        stroke: {
          bottom: '',
        },
      },
    },
    PackageCard: {
      alignment: 'left',
      hideIcons: false,
      radius: '4px',
      backgroundColor: '',
      color: '',
      shadow: '',
      stroke: {
        width: '',
        style: '',
        color: '',
      },
      label: {
        inside: false,
        backgroundColor: '',
        color: '',
        radius: '',
      },
      labelOffer: {
        size: '16px',
        lineHeight: '20px',
        legal: {
          size: '',
          lineHeight: '',
          opacity: '',
        },
      },
      title: {
        color: '',
        fontSize: '',
        lineHeight: '',
        fontWeight: '',
        fontFamily: '',
      },
      price: {
        centsUnderDuration: false,
        largeDollar: false,
        largeCents: false,
        duration: {
          underCents: false,
          underPrice: true,
        },
      },
    },
    Price: {
      color: '',
      noDecimal: null,
      dollarSign: {
        size: '',
        position: '', // top, bottom (default), center
        offsetY: '',
        offsetX: '',
        weight: '', // normal, bold
      },
      cents: {
        size: '',
        position: '', // top, bottom (default), center
        weight: '',
        offsetY: '',
        offsetX: '',
      },
      duration: {
        color: '',
        position: '', // top, bottom (default), center, underCents, underPrice
        weight: '',
        size: '',
        lineHeight: '',
        opacity: '',
        offsetY: '',
        offsetX: '',
        fontFamily: '',
      },
      disclaimer: {
        size: '',
      },
    },
  },
}
